import first from "lodash/first";
import type { FunctionComponent } from "react";
import { Fragment, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { notEmpty } from "shared/lib/utils";

import { ActionWidget } from "scmp-app/components/action-widget";
import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import { Spacer } from "scmp-app/components/common/spacer";
import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import type { YoutubeVolumeData } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/types";
import type { VideoPlayerYouTubeEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import { checkIfNewsSectionInFirstIndex } from "scmp-app/lib/section";
import type { videoPrimaryContent$key } from "scmp-app/queries/__generated__/videoPrimaryContent.graphql";

import {
  Container,
  Divider,
  FunctionBar,
  Headline,
  HeadlineEntityLink,
  InformationContainer,
  ReadFullStory,
  SectionBreadcrumb,
  SectionBreadcrumbLink,
  StoryButtonContainer,
  StyledSCMPYoutubeVideo,
  StyledTopicLink,
} from "./styles";

export type Props = {
  className?: string;
  enableAutoPlay?: boolean;
  ga4Position?: VideoPlayerYouTubeEventCustomizedParameters["position"];
  isMuted?: boolean;
  onEnded?: () => void;
  onVolumeChanged?: (data: YoutubeVolumeData) => void;
  reference?: videoPrimaryContent$key;
};

export const VideoPrimary: FunctionComponent<Props> = ({
  className,
  enableAutoPlay = true,
  ga4Position = "article_header",
  isMuted = true,
  onEnded,
  onVolumeChanged,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment videoPrimaryContent on Video {
        entityId
        headline
        socialHeadline
        videoPrimaryYoutubeId: youtubeId
        image(filter: { type: MAIN_IMAGE }) {
          style(filter: { style: "wide_landscape" }) {
            url
          }
        }
        sectionsV2 {
          value {
            ... on VideoSection {
              name
            }
            ...helpersCheckIfNewsSectionInFirstIndexSection
            ...entityLink
          }
        }
        topics {
          ...topicLinkTopic
        }
        videoSource {
          id
          type
        }
        ...actionWidgetContent
        ...entityLink
        ...shareArticle20Content
      }
    `,
    reference,
  );

  const title = useMemo(
    () => findFirstNonEmptyString(content?.socialHeadline, content?.headline),
    [content?.headline, content?.socialHeadline],
  );
  const topic = useMemo(() => first(content?.topics), [content?.topics]);

  const sections = useMemo(() => {
    const firstSectionGroup = first(content?.sectionsV2)?.value ?? [];
    return firstSectionGroup
      .slice(-2)
      .filter((section, index) => !checkIfNewsSectionInFirstIndex(section, index));
  }, [content?.sectionsV2]);

  const youtubeId = useMemo(
    () =>
      content?.videoPrimaryYoutubeId ??
      content?.videoSource?.find(source => source?.type === "youtube")?.id,
    [content?.videoPrimaryYoutubeId, content?.videoSource],
  );

  if (!notEmpty(content) || !notEmpty(youtubeId)) return null;

  return (
    <Container className={className}>
      <StyledSCMPYoutubeVideo
        enableAutoPlay={enableAutoPlay}
        entityId={content.entityId}
        gaData={{
          ga4ArticleEntityId: content.entityId,
          ga4Position,
        }}
        imageSource={content.image?.style?.url ?? undefined}
        index={0}
        initialVideoPlayMode={VideoPlayMode.Autoplay}
        isEnabledImaManager={true}
        isMuteVideo={isMuted}
        key={content.entityId}
        onEnded={onEnded}
        onVolumeChanged={onVolumeChanged}
        youtubeVideoId={youtubeId}
      />

      <Headline>
        <HeadlineEntityLink reference={content}>{title}</HeadlineEntityLink>
      </Headline>

      <InformationContainer>
        {topic && <StyledTopicLink preferShortName reference={topic} />}

        {notEmpty(sections) && (
          <SectionBreadcrumb>
            {sections.map(
              (section, index) =>
                section && (
                  <Fragment key={index}>
                    {index > 0 && " / "}
                    <SectionBreadcrumbLink reference={section}>
                      {section.name}
                    </SectionBreadcrumbLink>
                  </Fragment>
                ),
            )}
          </SectionBreadcrumb>
        )}

        <Spacer />

        <StoryButtonContainer>
          <ReadFullStory reference={content}>READ FULL STORY</ReadFullStory>
        </StoryButtonContainer>
      </InformationContainer>

      <Divider />

      <FunctionBar>
        <Share position="article_header" reference={content} />
        <ActionWidget contentCommentLinkMode="navigate" reference={content} variant="video" />
      </FunctionBar>
    </Container>
  );
};

VideoPrimary.displayName = "VideoPrimary";
